import { Loader2, CheckCircle2, AlertTriangle, WifiOff, RotateCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/apply/MultiSelect";
import { DISCIPLINES } from "@/lib/horseOptions";
import { HEIGHTS, normalizeHeight } from "@/lib/format";

const LABEL = "block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5";

// One shared hands.inches dropdown for every horse height field.
export function HeightSelect({ label, value, onChange, testid, disabled, required, error, hint }) {
  const raw = String(value ?? "").trim();
  const norm = normalizeHeight(raw);
  return (
    <div>
      <label htmlFor={testid} className={LABEL}>{label}{required && <span className="text-red-600"> *</span>}</label>
      <select id={testid} data-testid={testid} value={norm || raw} disabled={disabled} aria-invalid={!!error}
        onChange={(e) => onChange(e.target.value)}
        className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-stone-900 shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stone-500 disabled:bg-stone-100 ${error ? "border-red-400" : "border-stone-200"}`}>
        <option value="">Select height (hands)</option>
        {raw && !norm && <option value={raw}>Saved: {raw} — please choose a valid height</option>}
        {HEIGHTS.map((h) => <option key={h} value={h}>{h} hh</option>)}
      </select>
      {raw && !norm && <p data-testid={`${testid}-invalid`} className="mt-1 text-xs font-medium text-amber-700">Saved height “{raw}” isn't valid (hands.inches, .0–.3 only). Please choose the correct height.</p>}
      {hint && !error && <p className="mt-1 text-[11px] text-stone-400">{hint}</p>}
      {error && <p data-testid={`${testid}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// na: shows an N/A choice (stored as "N/A") for answers that genuinely don't apply; naFixed: shown as N/A and not editable.
export function Field({ label, value, onChange, testid, disabled, required, error, hint, inputMode, na, naFixed }) {
  const isNa = naFixed || String(value ?? "").trim().toUpperCase() === "N/A";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={testid} className={LABEL}>{label}{required && <span className="text-red-600"> *</span>}</label>
        {na && !naFixed && !disabled && (
          <button type="button" data-testid={`${testid}-na`} aria-pressed={isNa} onClick={() => onChange(isNa ? "" : "N/A")}
            className={`mb-1.5 px-2.5 h-6 rounded-full border text-[11px] font-semibold ${isNa ? "bg-amber-800 border-amber-800 text-white" : "border-stone-300 text-stone-600 hover:border-amber-700"}`}>N/A</button>
        )}
      </div>
      <Input id={testid} data-testid={testid} value={naFixed ? "N/A" : value ?? ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled || isNa} inputMode={inputMode}
        aria-invalid={!!error} className={`focus-visible:ring-amber-800 ${error ? "border-red-400" : "border-stone-200"}`} />
      {hint && !error && <p className="mt-1 text-[11px] text-stone-400">{hint}</p>}
      {error && <p data-testid={`${testid}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function DisciplineField({ info, onChange, disabled, error, otherError }) {
  const value = info?.disciplines || [];
  const id = "field-horse-disciplines";
  const labelOf = (k) => (k === "other" ? `Other${info?.discipline_other ? ` — ${info.discipline_other}` : ""}` : (DISCIPLINES.find(([o]) => o === k) || [k, k])[1]);
  return (
    <div className="sm:col-span-2 min-w-0">
      <span id={`${id}-label`} className={LABEL}>Discipline / Type<span className="text-red-600"> *</span></span>
      {disabled ? (
        <div data-testid={`${id}-readonly`} className="flex flex-wrap gap-1.5">
          {value.length ? value.map((k) => <span key={k} className="px-3 h-8 inline-flex items-center rounded-full bg-amber-50 border border-amber-300 text-sm text-amber-900">{labelOf(k)}</span>)
            : <span className="text-sm text-stone-400">{info?.horse_type || "None selected"}</span>}
        </div>
      ) : (
        <MultiSelect id={id} title="Discipline" options={DISCIPLINES} value={value} error={error} placeholder="Search or select disciplines"
          onChange={(v) => onChange({ disciplines: v })} />
      )}
      {!disabled && value.includes("other") && (
        <Input data-testid="field-horse-discipline_other" value={info?.discipline_other || ""} placeholder="Other discipline"
          onChange={(e) => onChange({ disciplines: value, discipline_other: e.target.value })} aria-invalid={!!otherError}
          className={`mt-2 focus-visible:ring-amber-800 ${otherError ? "border-red-400" : "border-stone-200"}`} />
      )}
      {error && <p data-testid={`${id}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
      {otherError && <p data-testid="field-horse-discipline_other-error" className="mt-1 text-xs text-red-600">Please describe the other discipline.</p>}
    </div>
  );
}

const STATUS = {
  saving: [Loader2, "Saving…", "text-stone-500", "animate-spin"],
  pending: [Loader2, "Saving…", "text-stone-500", "animate-spin"],
  saved: [CheckCircle2, "All changes saved", "text-emerald-700", ""],
  offline: [WifiOff, "Offline — saved on this device", "text-amber-800", ""],
  error: [AlertTriangle, "Unable to save", "text-red-700", ""],
  locked: [AlertTriangle, "Locked — not saved", "text-red-700", ""],
};

export function SaveStatus({ status, onRetry }) {
  const [Icon, text, tone, spin] = STATUS[status] || STATUS.saved;
  return (
    <div data-testid="autosave-status" data-status={status} aria-live="polite" className={`flex items-center gap-1 text-[11px] sm:text-xs font-semibold whitespace-nowrap ${tone}`}>
      <Icon className={`h-3.5 w-3.5 ${spin}`} />
      <span>{text}</span>
      {status === "error" && (
        <button type="button" data-testid="autosave-retry" onClick={onRetry} className="ml-1 inline-flex items-center gap-0.5 underline">
          <RotateCw className="h-3 w-3" />Retry
        </button>
      )}
    </div>
  );
}
